import { CAMPAIGN_TYPES, TENANT_ACCOUNT_INFO_TO_TAG_NAME } from 'common/constants';

import appSettings from '../../app-settings';
import {
  acceptSuggestedCampaign,
  createTemplate,
  getDefaultSectionTemplates,
  getTemplate,
  uploadHtml,
} from '../../api';
import {
  injectTemplateHtmlSections,
  injectTemplateDesignSections,
  replaceTemplateHtmlDonateButtonStyles,
  replaceTemplateDesignDonateButtonStyles,
} from '../../core/utils';

/**
 * Shared by the campaign drawer (emails/Drawer.jsx) and the onboarding warm-up editor
 * (onboarding/WarmUpEmailAccordion.jsx), so a suggested draft is personalised and accepted the same
 * way wherever the tenant opens it.
 */

const URL_REGEX = /https:\/\/(?:[^/]*\.)?supporterfund\.org([^\s"'<>\\]*)/g;

const pointUrlAtTenant = (account, campaignId) => (match) => {
  const url = new URL(match);
  url.hostname = `${account.donationPageSubdomain}.${appSettings.donationPageDomain}`;
  url.searchParams.set('campaignId', campaignId);
  return url.toString();
};

/**
 * Loads a campaign's template. For a SUGGESTED (shared, staff-authored) template it is personalised
 * for the tenant: donation page links point at the tenant's own page for this campaign,
 * organization merge tags are filled in, and the tenant's donate-button styles and default sections
 * are applied. Returns `{ design, html }` with `design` as a JSON string.
 */
export const getPersonalizedTemplate = async ({ templateId, version, suggested, campaignId, account }) => {
  const template = await getTemplate(templateId, version, suggested);
  let { design, html } = template;
  const replaceUrl = pointUrlAtTenant(account, campaignId);
  design = design.replace(URL_REGEX, replaceUrl);
  html = html.replace(URL_REGEX, replaceUrl);
  Object.keys(TENANT_ACCOUNT_INFO_TO_TAG_NAME).forEach((key) => {
    const mergeTag = `{{${TENANT_ACCOUNT_INFO_TO_TAG_NAME[key]}}}`;
    const mergeTagValue = account[key].replaceAll('\n', '<br />'); // Handle multiline values (like address)
    design = design.replaceAll(mergeTag, mergeTagValue);
    html = html.replaceAll(mergeTag, mergeTagValue);
  });

  if (suggested && account.emailPreferences.donateButton) {
    html = replaceTemplateHtmlDonateButtonStyles(html, account.emailPreferences.donateButton);
    design = replaceTemplateDesignDonateButtonStyles(design, account.emailPreferences.donateButton);
  }

  if (suggested && account.emailPreferences.sections) {
    const sectionTemplates = await getDefaultSectionTemplates(account.emailPreferences.sections);

    html = injectTemplateHtmlSections(html, sectionTemplates);
    design = injectTemplateDesignSections(design, sectionTemplates);
  }

  return { design, html };
};

/**
 * Accepts a suggested campaign: copies the (personalised or edited) template into a template the
 * tenant owns, then flips the campaign from `suggested` to `draft`. `design` must be a JSON string.
 * Returns the updated campaign from PATCH /campaigns/accept-suggested.
 */
export const acceptSuggestedCampaignWithTemplate = async ({ campaign, design, html, emailConfig }) => {
  const { uploadUrl, templateId } = await createTemplate(
    design,
    { type: CAMPAIGN_TYPES.single },
    emailConfig ?? campaign.emailConfig,
  );
  await uploadHtml(uploadUrl, html);
  return acceptSuggestedCampaign(campaign.campaignId, templateId);
};
